import { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "utils/@reduxjs/toolkit";
import { useInjectReducer } from "utils/redux-injectors";
import { createApi } from "@reduxjs/toolkit/query/react";
import { endpoints, baseQuery } from "utils/api/endpoints";
import { DigitalSecurityCurrentAndUpgradePlansState, DigitalSecurityPlansResponse } from "./types";

export const initialState: DigitalSecurityCurrentAndUpgradePlansState = {
  loading: false,
  error: null,
  data: null,
};

const slice = createSlice({
  name: "digitalSecurityCurrentAndUpgradePlans",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setData(state, action: PayloadAction<DigitalSecurityPlansResponse>) {
      state.data = action.payload;
    },
  },
});

// Create RTK Query API
export const api = createApi({
  reducerPath: "digitalSecurityCurrentAndUpgradePlansApi",
  baseQuery,
  tagTypes: ["DigitalSecurityPlans"],
  endpoints: (builder) => ({
    getDigitalSecurityCurrentAndUpgradePlans: builder.query<DigitalSecurityPlansResponse, { customerId: string }>({
      query: (params) => ({
        ...endpoints.digitalSecurityCurrentAndUpgradePlans,
        params,
      }),
      providesTags: ["DigitalSecurityPlans"],
      // Add retry logic
      extraOptions: {
        maxRetries: 3,
        backoff: (attempt) => Math.min(1000 * (2 ** attempt), 30000),
      },
      // Transform response to handle errors consistently
      transformErrorResponse: (response: { status: number, data: any }) => {
        return {
          status: response.status,
          message: response.data?.message || 'An error occurred while fetching digital security plans',
        };
      },
    }),
  }),
});

export const { actions: digitalSecurityCurrentAndUpgradePlansActions, reducer: digitalSecurityCurrentAndUpgradePlansReducer } = slice;

export const useDigitalSecurityCurrentAndUpgradePlansSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  
  return { 
    actions: slice.actions,
    ...api
  };
};

/**
 * Example Usage:
 *
 * export function MyComponent() {
 *   const { useLazyGetDigitalSecurityCurrentAndUpgradePlansQuery } = useDigitalSecurityCurrentAndUpgradePlansSlice();
 *   const [getPlans, { data, isLoading, isError }] = useLazyGetDigitalSecurityCurrentAndUpgradePlansQuery();
 *
 *   useEffect(() => {
 *     // Call the API
 *     getPlans({ customerId: "123" });
 *   }, []);
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (isError) return <div>Error loading digital security plans</div>;
 *
 *   return (
 *     <div>
 *       {data?.currentPlans?.map(plan => (
 *         <div key={plan.id}>
 *           {plan.name} - {plan.price}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 */ 